import { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Stack,
} from "@mui/material";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { useSnackbar } from "notistack";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import {
  useAllWhatsappTemplates,
  useSyncWhatsappTemplateStatus,
  useSyncWhatsappTemplatesFromMeta,
  useSyncWhatsappQuality,
  useDeleteWhatsappTemplate,
  useWhatsappNumbers,
} from "../../hooks/useWhatsapp";
import TemplateBuilderDialog from "./TemplateBuilderDialog";
import TemplatePreviewDialog from "./TemplatePreviewDialog";

// Aligned to the app's own theme tokens (src/components/common/Colors.js) rather than
// arbitrary close-but-different hexes — success.main/danger.main are the actual brand
// green/red used for status semantics everywhere else in the app.
const STATUS_COLORS = {
  APPROVED: { bg: "rgba(9,172,49,0.12)", color: "#09AC31", dot: "#09AC31" },
  PENDING: { bg: "rgba(250,153,5,0.14)", color: "#B36B00", dot: "#FA9905" },
  REJECTED: { bg: "rgba(227,76,20,0.10)", color: "#E34C14", dot: "#E34C14" },
  PAUSED: { bg: "#F1F1F1", color: "#666", dot: "#999" },
  DISABLED: { bg: "#F1F1F1", color: "#666", dot: "#999" },
};

const CATEGORY_ICON = {
  UTILITY: VerifiedUserOutlinedIcon,
  MARKETING: CampaignOutlinedIcon,
  AUTHENTICATION: LocalOfferOutlinedIcon,
};

// Meta's documented quality values — unconfirmed against a real payload yet (see
// helpers/whatsappHelper.js's BAD_PHONE_QUALITY), so anything outside this map still
// renders (just in a neutral color) rather than looking broken.
const QUALITY_COLORS = {
  GREEN: { bg: "rgba(9,172,49,0.12)", color: "#09AC31" },
  YELLOW: { bg: "rgba(250,153,5,0.14)", color: "#B36B00" },
  RED: { bg: "rgba(227,76,20,0.10)", color: "#E34C14" },
  FLAGGED: { bg: "rgba(227,76,20,0.10)", color: "#E34C14" },
  RESTRICTED: { bg: "rgba(227,76,20,0.10)", color: "#E34C14" },
};

function QualityChip({ rating }) {
  if (!rating) return null;
  const c = QUALITY_COLORS[rating] || { bg: "#F1F1F1", color: "#666" };
  return <Chip size="small" label={rating} sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 10 }} />;
}

// business_verification_status is the actual lever for raising a number's messaging-limit
// tier (Meta docs: developers.facebook.com/documentation/business-messaging/whatsapp/messaging-limits)
// — surfaced here so "why is this number stuck at TIER_250" has an answer without digging
// into Meta's Business Manager. Values confirmed live: "verified", "not_verified",
// "pending_submission", "pending_review", "rejected", "expired" (all lowercase).
const VERIFICATION_COLORS = {
  verified: { bg: "rgba(9,172,49,0.12)", color: "#09AC31" },
  pending_submission: { bg: "rgba(250,153,5,0.14)", color: "#B36B00" },
  pending_review: { bg: "rgba(250,153,5,0.14)", color: "#B36B00" },
  not_verified: { bg: "#F1F1F1", color: "#666" },
  rejected: { bg: "rgba(227,76,20,0.10)", color: "#E34C14" },
  expired: { bg: "rgba(227,76,20,0.10)", color: "#E34C14" },
};

function VerificationChip({ status }) {
  if (!status) return null;
  const c = VERIFICATION_COLORS[status] || { bg: "#F1F1F1", color: "#666" };
  const label = status.replace(/_/g, " ");
  return (
    <Tooltip title="Business verification status — raising this is what actually raises the messaging-limit tier">
      <Chip
        size="small"
        icon={<VerifiedUserOutlinedIcon sx={{ fontSize: "13px !important" }} />}
        label={label}
        sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 10, textTransform: "capitalize" }}
      />
    </Tooltip>
  );
}

// One row per registered number — usage/quality/tier are all things this whole session's
// worth of safeguards depend on, so they need to actually be visible somewhere, not just
// tracked silently in the database.
function NumberHealthStrip({ numbers }) {
  if (!numbers.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
      {numbers.map((n) => {
        const nearLimit = n.recentUniqueRecipients >= n.messagingLimit * 0.8;
        return (
          <Box
            key={n.phoneNumberId}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: "10px",
              bgcolor: "#fff",
              border: "1px solid rgba(0,0,0,0.07)",
            }}
          >
            <PhoneIphoneOutlinedIcon sx={{ fontSize: 15, color: "#6B7280" }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontFamily: "Quicksand" }}>{n.displayPhoneNumber}</Typography>
            <QualityChip rating={n.qualityRating} />
            {(n.businessTier || n.currentTier) && (
              <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>{n.businessTier || n.currentTier}</Typography>
            )}
            <VerificationChip status={n.businessVerificationStatus} />
            <Typography sx={{ fontSize: 11, color: nearLimit ? "#E34C14" : "#9CA3AF", fontWeight: nearLimit ? 700 : 400 }}>
              {n.recentUniqueRecipients}/{n.messagingLimit} in 24h
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function StatusChip({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.PENDING;
  return (
    <Chip
      size="small"
      icon={<Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: c.dot, ml: "8px !important" }} />}
      label={status}
      sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 11, "& .MuiChip-icon": { mr: -0.5 } }}
    />
  );
}

function findBodyText(template) {
  const body = (template.components || []).find((c) => c.type === "BODY" || c.type === "body");
  return body?.text || "";
}

function TemplateCard({ template, numberLabel, onSyncStatus, onEdit, onPreview, onDelete }) {
  // Meta only allows editing a template while it's REJECTED — every other status errors
  // with "Message templates can only be edited if they have been rejected", so anything
  // else only gets a read-only preview.
  const canEdit = template.status === "REJECTED";
  const CategoryIcon = CATEGORY_ICON[template.category] || VerifiedUserOutlinedIcon;

  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: "1px solid rgba(0,0,0,0.07)",
        bgcolor: "#fff",
        p: 2,
        // No height:100% — with row-stretch, a percentage height on a grid item sitting
        // in an auto-sized row track is ambiguous and resolves differently per browser,
        // which is what made adjacent rows' borders touch/overlap. Each card is sized to
        // its own content instead (grid opts out of stretch below), which is fully
        // deterministic and needs no percentage height at all.
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.15s ease, transform 0.15s ease",
        "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.08)", transform: "translateY(-2px)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
        <Box sx={{ width: 32, height: 32, borderRadius: "9px", bgcolor: "rgba(37,211,102,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <CategoryIcon sx={{ fontSize: 17, color: "#25D366" }} />
        </Box>
        {/* Full name, wrapped over up to 2 lines — a single-line ellipsis made every
            jaspers_market_* template read as an indistinguishable "jaspers_mark…". */}
        <Typography
          title={template.name}
          sx={{
            fontWeight: 700,
            fontFamily: "Quicksand",
            fontSize: 14,
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          {template.name}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>
          {template.category} · {template.language}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <QualityChip rating={template.qualityRating} />
          <StatusChip status={template.status} />
        </Stack>
      </Box>

      {/* Which of our numbers this template belongs to — the whole reason this prop
          exists is that a template can't be identified by name alone once there's more
          than one WhatsApp number, since names aren't unique across WABAs. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <PhoneIphoneOutlinedIcon sx={{ fontSize: 13, color: "#9CA3AF" }} />
        <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>{numberLabel || "Unknown number"}</Typography>
      </Box>

      {/* Fixed height (not flex:1) — a CSS grid stretches every card in a row to match
          the tallest one, so a short-text card next to a long one used to end up with a
          large dead gap between its text and the action icons below. */}
      <Box sx={{ bgcolor: "#F7F8FA", borderRadius: "10px", p: 1.25, height: 84, mb: 1, overflow: "hidden" }}>
        <Typography sx={{ fontSize: 12.5, color: "#3B4A54", whiteSpace: "pre-wrap", lineHeight: 1.5, fontFamily: "Quicksand", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {findBodyText(template) || <span style={{ color: "#B0B7BF", fontStyle: "italic" }}>No body text</span>}
        </Typography>
      </Box>

      {template.status === "REJECTED" && template.rejectionReason && (
        <Typography sx={{ fontSize: 11, color: "#E34C14", mb: 1 }}>
          Rejected: {template.rejectionReason}
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, borderTop: "1px solid rgba(0,0,0,0.06)", pt: 1, mt: "auto" }}>
        <Tooltip title="Check latest status from Meta">
          <IconButton size="small" onClick={() => onSyncStatus(template._id)}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canEdit ? (
          <Tooltip title="Edit (rejected templates can be fixed and resubmitted)">
            <IconButton size="small" onClick={() => onEdit(template)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Preview — only rejected templates can be edited">
            <IconButton size="small" onClick={() => onPreview(template)}>
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => onDelete(template._id, template.name)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

function WhatsAppTemplatesPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading } = useAllWhatsappTemplates();
  const { data: numbersData } = useWhatsappNumbers();
  const syncStatusMutation = useSyncWhatsappTemplateStatus();
  const syncAllMutation = useSyncWhatsappTemplatesFromMeta();
  const syncQualityMutation = useSyncWhatsappQuality();
  const deleteMutation = useDeleteWhatsappTemplate();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewingTemplate, setPreviewingTemplate] = useState(null);
  const [numberFilter, setNumberFilter] = useState("all");

  const allTemplates = data?.templates || [];
  const numbers = numbersData?.tokens || [];
  // wabaId -> "+91 98116 29081" — a template's own record only stores wabaId, so this is
  // what actually answers "which number does this belong to" anywhere it's shown.
  const numberLabelByWaba = Object.fromEntries(numbers.map((n) => [n.wabaId, n.displayPhoneNumber]));

  const templates = numberFilter === "all" ? allTemplates : allTemplates.filter((t) => t.wabaId === numberFilter);

  const handleSyncStatus = async (id) => {
    try {
      await syncStatusMutation.mutateAsync(id);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to check status", { variant: "error" });
    }
  };

  const handleSyncAll = async () => {
    try {
      const res = await syncAllMutation.mutateAsync();
      // Also refresh number health (quality rating / tier) — same "sync from Meta" action
      // covers both, so there's one button instead of two near-identical ones. Best-effort:
      // a failure here shouldn't hide the template sync's own success.
      syncQualityMutation.mutate();
      enqueueSnackbar(`Synced ${res.synced} template${res.synced === 1 ? "" : "s"} from Meta`, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to sync from Meta", { variant: "error" });
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"? This also removes it from Meta.`)) return;
    try {
      await deleteMutation.mutateAsync(id);
      enqueueSnackbar("Template deleted", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to delete template", { variant: "error" });
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)" }}>
      <DashboardHeaderSection hideSearch />
      <Container sx={{ marginTop: 8, pb: 6, pl: { xs: 2, sm: 3, lg: "108px" }, pr: { xs: 2, sm: 3 } }} maxWidth="xl">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 1 }}>
            <WhatsAppIcon sx={{ color: "#25D366" }} /> WhatsApp Templates
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {numbers.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select value={numberFilter} onChange={(e) => setNumberFilter(e.target.value)} displayEmpty>
                  <MenuItem value="all">All numbers</MenuItem>
                  {numbers.map((n) => (
                    <MenuItem key={n.wabaId} value={n.wabaId}>
                      {n.displayPhoneNumber}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              variant="outlined"
              startIcon={syncAllMutation.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={handleSyncAll}
              disabled={syncAllMutation.isPending}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 700,
                color: "#FF5200",
                borderColor: "rgba(255,82,0,0.35)",
                "&:hover": { borderColor: "#FF5200", bgcolor: "rgba(255,82,0,0.08)" },
              }}
            >
              Sync from Meta
            </Button>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setCreateOpen(true)}
              sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none", bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
            >
              New Template
            </Button>
          </Box>
        </Box>

        <NumberHealthStrip numbers={numbers} />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : templates.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              borderRadius: "16px",
              border: "1px dashed rgba(0,0,0,0.12)",
              bgcolor: "rgba(255,255,255,0.5)",
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 36, color: "#25D36680", mb: 1 }} />
            <Typography sx={{ fontFamily: "Quicksand", fontWeight: 700, color: "#1C1C2E", mb: 0.5 }}>No templates yet</Typography>
            <Typography sx={{ fontFamily: "Quicksand", color: "#6B7280", fontSize: 13.5 }}>
              Create one, or if you already have templates in WhatsApp Manager, click "Sync from Meta".
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 2,
              // Opt out of the default row-stretch — each card sizes to its own content
              // instead of being stretched to match its tallest row-neighbor, which is
              // what caused the cross-browser border-touching/overlap bug.
              alignItems: "start",
            }}
          >
            {templates.map((t) => (
              <TemplateCard
                key={t._id}
                template={t}
                numberLabel={numberLabelByWaba[t.wabaId]}
                onSyncStatus={handleSyncStatus}
                onEdit={setEditingTemplate}
                onPreview={setPreviewingTemplate}
                onDelete={handleDelete}
              />
            ))}
          </Box>
        )}
      </Container>

      <TemplateBuilderDialog
        open={createOpen || Boolean(editingTemplate)}
        onClose={() => {
          setCreateOpen(false);
          setEditingTemplate(null);
        }}
        template={editingTemplate}
      />

      <TemplatePreviewDialog
        open={Boolean(previewingTemplate)}
        onClose={() => setPreviewingTemplate(null)}
        template={previewingTemplate}
      />
    </Box>
  );
}

export default WhatsAppTemplatesPage;
